// packages/ui/src/components/chat/useDraggableFab.ts
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

// Shared, browser-safe (no next/*, no MUI) drag hook for the floating chat
// launcher. Used by EVERY surface of the chat app — the backoffice support
// widget AND the embeddable website widget (and the future public-site bot) —
// so the launcher is movable everywhere, not just in the backoffice.

const FAB_SIZE = 48; // medium MUI Fab; the edge clamp is approximate either way
const EDGE_MARGIN = 16; // keep this far from the viewport edges
const DRAG_THRESHOLD = 4; // px of movement before a press counts as a drag

export interface FabPosition {
  x: number;
  y: number;
}

/** Where to position the fixed container holding the Fab: an explicit top/left
 *  once placed, or a corner default (left- or right-anchored) before any drag. */
type ContainerPos =
  | { left: number; top: number }
  | { left: number; bottom: number }
  | { right: number; bottom: number };

export interface DraggableFab {
  containerPos: ContainerPos;
  /** Open the panel toward the side/with the room — keeps it on-screen wherever
   *  the Fab is dragged. */
  side: 'left' | 'right';
  vertical: 'up' | 'down';
  dragging: boolean;
  fabProps: {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
  };
  /** Wrap the Fab's click so the press that *ended a drag* doesn't also toggle
   *  the panel. A real click (no drag) calls `toggle`. */
  guardClick: (toggle: () => void) => void;
}

export interface UseDraggableFabOptions {
  /** localStorage key for the remembered position — distinct per surface so the
   *  backoffice and website widgets don't share a corner. */
  storageKey: string;
  /** Which bottom corner the Fab sits in before the user drags it. The
   *  backoffice defaults left (clear of the booking Save button); the website
   *  widget defaults right (the conventional chat corner). */
  defaultSide?: 'left' | 'right';
}

const clamp = (v: number, min: number, max: number): number => Math.min(Math.max(v, min), max);

function maxLeft(): number {
  return Math.max(EDGE_MARGIN, window.innerWidth - FAB_SIZE - EDGE_MARGIN);
}
function maxTop(): number {
  return Math.max(EDGE_MARGIN, window.innerHeight - FAB_SIZE - EDGE_MARGIN);
}

/**
 * Makes a floating Fab draggable and remembers where the user puts it
 * (localStorage), defaulting to a bottom corner. Also reports which way the
 * panel should open so it stays on-screen from any corner. Click vs. drag is
 * disambiguated by a small movement threshold.
 */
export function useDraggableFab(options: UseDraggableFabOptions): DraggableFab {
  const { storageKey, defaultSide = 'left' } = options;
  const [pos, setPosState] = useState<FabPosition | null>(null);
  const posRef = useRef<FabPosition | null>(null);
  const setPos = useCallback((p: FabPosition | null) => {
    posRef.current = p;
    setPosState(p);
  }, []);

  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const startRef = useRef({ px: 0, py: 0, baseX: 0, baseY: 0 });
  const [dragging, setDragging] = useState(false);

  // Restore a saved position (clamped into the current viewport).
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<FabPosition>;
      if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
        setPos({ x: clamp(parsed.x, EDGE_MARGIN, maxLeft()), y: clamp(parsed.y, EDGE_MARGIN, maxTop()) });
      }
    } catch {
      // ignore malformed/unavailable storage — fall back to the default corner
    }
  }, [setPos, storageKey]);

  // Keep a placed Fab on-screen when the window resizes.
  useEffect(() => {
    const onResize = () => {
      const p = posRef.current;
      if (!p) return;
      setPos({ x: clamp(p.x, EDGE_MARGIN, maxLeft()), y: clamp(p.y, EDGE_MARGIN, maxTop()) });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setPos]);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return; // primary button only
    const rect = e.currentTarget.getBoundingClientRect();
    startRef.current = { px: e.clientX, py: e.clientY, baseX: rect.left, baseY: rect.top };
    draggingRef.current = true;
    movedRef.current = false;
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!draggingRef.current) return;
      const dx = e.clientX - startRef.current.px;
      const dy = e.clientY - startRef.current.py;
      if (!movedRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      movedRef.current = true;
      setDragging(true);
      setPos({
        x: clamp(startRef.current.baseX + dx, EDGE_MARGIN, maxLeft()),
        y: clamp(startRef.current.baseY + dy, EDGE_MARGIN, maxTop()),
      });
    },
    [setPos],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      if (movedRef.current) {
        setDragging(false);
        const p = posRef.current;
        if (p) {
          try {
            window.localStorage.setItem(storageKey, JSON.stringify(p));
          } catch {
            // ignore — position simply won't persist across reloads
          }
        }
      }
      // A press with no drag falls through to the Fab's onClick (guardClick → toggle).
    },
    [storageKey],
  );

  const guardClick = useCallback((toggle: () => void) => {
    if (movedRef.current) {
      movedRef.current = false; // consume the drag; don't toggle
      return;
    }
    toggle();
  }, []);

  // Derive the container anchor + the panel's open direction.
  const hasWindow = typeof window !== 'undefined';
  const containerPos: ContainerPos = pos
    ? { left: pos.x, top: pos.y }
    : defaultSide === 'right'
      ? { right: EDGE_MARGIN, bottom: EDGE_MARGIN }
      : { left: EDGE_MARGIN, bottom: EDGE_MARGIN };

  const defaultLeft = hasWindow && defaultSide === 'right'
    ? window.innerWidth - FAB_SIZE - EDGE_MARGIN
    : EDGE_MARGIN;
  const fabLeft = pos ? pos.x : defaultLeft;
  const fabTop = pos ? pos.y : hasWindow ? window.innerHeight - FAB_SIZE - EDGE_MARGIN : 0;
  const vw = hasWindow ? window.innerWidth : 0;
  const vh = hasWindow ? window.innerHeight : 0;
  const side: 'left' | 'right' = vw > 0 && fabLeft + FAB_SIZE / 2 > vw / 2 ? 'right' : 'left';
  const vertical: 'up' | 'down' = vh > 0 && fabTop + FAB_SIZE / 2 < vh / 2 ? 'down' : 'up';

  return { containerPos, side, vertical, dragging, fabProps: { onPointerDown, onPointerMove, onPointerUp }, guardClick };
}
